import CloseIcon from '@mui/icons-material/Close'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material'
import { PrepTimesResource, updatePrepTimes } from 'api'
import { ChangeEvent, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import outline_minus_button from 'assets/icons/outline_minus_button.svg'
import Chevron_Down from 'assets/icons/Chevron-Down.svg'
import InfoCircle from 'assets/icons/info-circle.svg'
import { AlertCircle, InfoToolTip, ErrorMessage, Text, SubText, AddTier, Cancel, EditorDescription, Footer, Icon, InputContainer, OrderThrottleHeader, OrderThrottleSubHeader, PrimaryButton, SubtotalHeaderContainer, SubtotalStart, Tiers, Title, SubtotalContainer, Divider } from './styles'

type Tier = {
    lower_price_limit: number
    upper_price_limit: number
    prep_minutes: number // prep time for each tier must increment by at least 5 minutes. And should be a min of 25
    id?: number // tiers with out an `id` are created (and given an id) when sent to the BE
    _destroy?: boolean // tiers with `_destroy` are deleted when sent to the BE
    subtotalError: string
    prepTimeError: string
    tempId: number // newly added tiers are given a tempId for ref. This will not be sent to the BE
}

interface Tiers {
    [id: string]: Tier
}

type Props = {
    partnerId: string | undefined
    platformPrepTimes: PrepTimesResource[] | []
    showModal: boolean
    toggleModal: () => void
}

export const OrderThrottleEditor = ({
    partnerId,
    platformPrepTimes,
    showModal,
    toggleModal,
}: Props) => {
    const [tiers, setTiers] = useState<Tiers>({}) // stores all tiers incl. tiers pending delete (`_destroy: true`)
    const [displayTiers, setDisplayTiers] = useState<Tier[]>([])
    let userInput: number

    /* Ensure uuid is always a number */
    const numericUuid = () => {
        const numericString = uuidv4().replace(/[^0-9]/g, '')
        const trimmedString = numericString.slice(0, 15)
        return parseInt(trimmedString, 10)
    }

    // Initialize/Reset tiers when modal opens or platformPrepTimes changes
    useEffect(() => {
        if (!showModal) return // Only initialize when modal is actually open
        
        // Always start with a fresh state - this clears any unsaved changes
        if (platformPrepTimes.length > 0) {
            // set prep times pulled from DB
            const setInitalTiers: { [key: number]: any } = {}
            const hasValidTiers = platformPrepTimes.some(p => !p.attributes._destroy)
            
            platformPrepTimes.forEach((p) => {
                const tempId = numericUuid()
                setInitalTiers[parseInt(p.id)] = {
                    id: p.id,
                    lower_price_limit: p.attributes.lower_price_limit,
                    upper_price_limit: p.attributes.upper_price_limit,
                    prep_minutes: p.attributes.prep_minutes,
                    kind: 'price_based',
                    _destroy: false,
                    subtotalError: '',
                    prepTimeError: '',
                    tempId
                }
                return
            })
            
            // If all existing tiers are marked for destruction, add a fresh default tier
            if (!hasValidTiers) {
                const tempId = numericUuid()
                setInitalTiers[tempId] = {
                    lower_price_limit: 0,
                    upper_price_limit: 0,
                    prep_minutes: 25,
                    subtotalError: '',
                    prepTimeError: '',
                    tempId,
                }
            }
            
            // Completely replace the tiers state (not merge with existing)
            setTiers(setInitalTiers)
        } else {
            // if no prep times returned from DB, the first tier displayed will have default values
            const tempId = numericUuid() // new tiers wont have DB id's, so create a tempId.
            setTiers({
                [tempId]: {
                    lower_price_limit: 0,
                    upper_price_limit: 0,
                    prep_minutes: 25,
                    subtotalError: '',
                    prepTimeError: '',
                    tempId,
                },
            })
            return
        }
    }, [platformPrepTimes, showModal])

    const handleInputChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        tierId: number,
    ) => {
        if (!tierId) return

        e.preventDefault()
        userInput = parseFloat(e.target.value) || 0
        const currentTierIndex = displayTiers.indexOf(tiers[tierId])
        let tierToUpdateId: number | undefined = undefined
        if (displayTiers[currentTierIndex + 1]?.id || displayTiers[currentTierIndex + 1]?.tempId) {
            tierToUpdateId = displayTiers[currentTierIndex + 1]?.id || displayTiers[currentTierIndex + 1]?.tempId
        }

        /** increment the total for the next tier by $1 to account for the entire range. Users must input whole numbers */
        const newTotal = userInput + 1

        const validationError =
            userInput <= tiers[tierId].lower_price_limit
                ? 'Upper price limit must be greater than lower price limit.'
                : displayTiers[currentTierIndex - 1] &&
                    userInput <= displayTiers[currentTierIndex - 1].upper_price_limit
                    ? 'Upper price limit must be greater than the previous tier.'
                    : ''

        setTiers((prevTiers) => ({
            ...prevTiers,
            [tierId]: {
                ...prevTiers[tierId],
                upper_price_limit: userInput,
                subtotalError: validationError,
            },
            ...(tierToUpdateId &&
                prevTiers[tierToUpdateId] && {
                [tierToUpdateId]: {
                    ...prevTiers[tierToUpdateId],
                    lower_price_limit: validationError
                        ? prevTiers[tierToUpdateId].lower_price_limit
                        : newTotal,
                },
            }),
        }))
    }

    const handlePrepTimeChange = (e: SelectChangeEvent<number>, tierId: number) => {
        if (!tierId) return
        e.preventDefault()
        const currentTierIndex = displayTiers.indexOf(tiers[tierId])
        const prevTierPrepTime = displayTiers[currentTierIndex - 1]?.prep_minutes || 0

        const validationError =
            parseInt(e.target.value.toString()) <= prevTierPrepTime
                ? 'Prep time must be greater than the previous tier.'
                : ''

        /* Update selected value for dropdown */
        setTiers((prevTiers) => ({
            ...prevTiers,
            [tierId]: {
                ...prevTiers[tierId],
                prep_minutes: parseInt(e.target.value.toString()),
                prepTimeError: validationError,
            },
        }))
    }

    const addTier = () => {
        const tempId = numericUuid()
      
        if (displayTiers.length === 0) {
            setTiers((prevTiers) => ({
                ...prevTiers,
                [tempId]: {
                    lower_price_limit: 0,
                    upper_price_limit: 0,
                    prep_minutes: 25,
                    subtotalError: '',
                    prepTimeError: '',
                    tempId,
                },
            }))
            return
        }
 
        const maxUpperLimit = Math.max(...displayTiers.map(tier => tier.upper_price_limit || 0))
        const maxPrepTime = Math.max(...displayTiers.map(tier => tier.prep_minutes))
        
        const startPrepTime = maxPrepTime + 5
        const startPrice = maxUpperLimit > 0 ? maxUpperLimit + 1 : 0

        setTiers((prevTiers) => ({
            ...prevTiers,
            [tempId]: {
                lower_price_limit: startPrice,
                upper_price_limit: 0,
                prep_minutes: startPrepTime,
                subtotalError: '',
                prepTimeError: '',
                tempId,
            },
        }))
    }

    const removeTier = (tierId: number | undefined, tempId: number) => {
        if (tierId) {
            // if tier has an id, it exists in the DB and needs to be removed via `_destroy: true`
            setTiers((prevTiers) => ({
                ...prevTiers,
                [tierId]: {
                    ...prevTiers[tierId],
                    _destroy: true,
                },
            }))
        } else {
            // if tier has no id, it can be filtered out of the payload
            setTiers((prevTiers) => {
                // Filter out the tier to remove
                const newTiersArray = Object.entries(prevTiers).filter(
                    ([key]) => key !== tempId?.toString(),
                )
                // Create a new object with updated indexes
                const newTiers: Tiers = {}
                newTiersArray.forEach(([_, value]) => {
                    const currentId = value.id || value.tempId
                    newTiers[currentId] = value
                })
                return newTiers // Return the new tiers object
            })
        }
        return
    }

    useEffect(() => {
        const displayArray: Tier[] = []
        Object.values(tiers).map((t) => {
            if (!t._destroy) {
                displayArray.push(t)
            }
        })
        displayArray.sort((a, b) => a.lower_price_limit - b.lower_price_limit)
        
        setDisplayTiers(displayArray)
    }, [tiers])

    const formatTiers = () => {
        const formattedTiers = Object.keys(tiers).map((t) => {
            const currentTier = tiers[parseInt(t)]
            return {
                id: currentTier?.id,
                lower_price_limit: currentTier.lower_price_limit,
                upper_price_limit: currentTier.upper_price_limit,
                prep_minutes: currentTier.prep_minutes,
                kind: 'price_based',
                _destroy: currentTier._destroy || false,
            }
        }).filter((tier) => {
            // If tier is marked for destruction but has no id, it was never saved, so exclude it
            // If tier has an id and is marked for destruction, include it so backend can delete it
            // If tier is not marked for destruction, include it
            return !(tier._destroy && !tier.id)
        })
        
        return {
            prep_times_attributes: [...formattedTiers],
        }
    }

    // If any validation fails, set the updated tiers with errors and show a message
    const validateOnSave = () => {
        let isValid = true
        const updatedTiers = { ...tiers }

        // Only validate tiers that are not marked for destruction (displayTiers)
        // This ensures we only validate tiers that will actually be active
        displayTiers.forEach((tier, index) => {
            const tierKey = tier.id?.toString() || tier.tempId.toString()
            const currentTier = updatedTiers[tierKey]
            const prevTier = index > 0 ? displayTiers[index - 1] : null

            // Reset error messages before validation
            currentTier.subtotalError = ''
            currentTier.prepTimeError = ''

            // Validate that the upper price limit is greater than the lower price limit
            if (currentTier.upper_price_limit <= currentTier.lower_price_limit) {
                currentTier.subtotalError = `Upper price limit must be greater than the lower price limit.`
                isValid = false
            }

            // Only validate against previous tier if this is not the first tier
            if (prevTier) {
                // Validate that the upper price limit for this tier is greater than the previous tier's upper price limit
                if (currentTier.upper_price_limit <= prevTier.upper_price_limit) {
                    currentTier.subtotalError = `Upper price limit must be greater than the upper price limit for the previous tier.`
                    isValid = false
                }

                // Validate that the prep time for this tier is greater than the previous tier's prep time
                if (currentTier.prep_minutes <= prevTier.prep_minutes) {
                    currentTier.prepTimeError = `Prep time must be greater than the prep time for the previous tier.`
                    isValid = false
                }
            }

            // Validate that upper and lower price limits are not empty or NaN
            if (isNaN(currentTier.upper_price_limit)) {
                currentTier.subtotalError = `Upper and lower price limits must be valid numbers.`
                isValid = false
            }
        })
        return {
            isValid,
            updatedTiers,
        }
    }

    const handleSave = async () => {
        if (!partnerId) return

        const { isValid, updatedTiers } = validateOnSave()
        if (!isValid) {
            setTiers(updatedTiers)
            toast.error(`Error updating prep times`, { autoClose: 5000 })
            return
        }

        // If all validations pass, format & save the data
        const formattedTiers = formatTiers()
        try {
            await updatePrepTimes(parseInt(partnerId), formattedTiers)
            toast.success('Order throttling settings saved successfully!', { autoClose: 3000 })
            toggleModal()
        } catch (e) {
            toast.error(`Error updating prep times: ${e}`, { autoClose: 5000 })
        }
    }

    return (
        <Dialog
            data-testid={'orderThrottleEditor'}
            open={showModal}
            onClose={toggleModal}
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    gap: '40px',
                    padding: '32px',
                    maxWidth: '600px',
                    maxHeight: '623px'
                },
            }}
        >
            <DialogTitle sx={{ padding: 0 }}>
                <OrderThrottleHeader>
                    <OrderThrottleSubHeader>
                        <Title>Order Throttling</Title>
                        <IconButton
                            aria-label='close'
                            onClick={toggleModal}
                            sx={{ color: '#000000' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </OrderThrottleSubHeader>

                    <EditorDescription>
                        Order Throttling enables dynamic preparation time adjustments based on order subtotals. Configure tiers in ascending order with each tier having higher values than the previous one. Changes must be saved to take effect.
                    </EditorDescription>
                </OrderThrottleHeader>

            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    border: 'none',
                    padding: 0
                }}
            >
                <Box sx={{
                    mt: 2, gap: '16px',
                    flexDirection: 'column',
                    display: 'flex'
                }}>
                    {displayTiers.map((tier, index) => (
                        <div key={tier.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: '8px' }}>
                                <Tiers>{`Tier ${index + 1}`}</Tiers>

                                <div>
                                    <SubtotalContainer>
                                        <SubtotalHeaderContainer>
                                            <Text>
                                                Order Subtotal
                                            </Text>
                                            <SubText>
                                                (required)
                                            </SubText>
                                        </SubtotalHeaderContainer>
                                        <InputContainer
                                            data-testid={'upper_price_limit'}
                                            // overlay the lower_price_limit above the text field component
                                            style={{ position: 'relative', display: 'inline-block' }}
                                        >
                                            <SubtotalStart>
                                                {`$${tier.lower_price_limit} - $`}
                                                <InfoToolTip content="Each maximum order subtotal value includes all cents up to the next whole dollar amount (e.g., a tier ending at $35 covers up to $35.99). Tiers must be configured in ascending order and saved to commit changes. Closing without saving will reset all modifications." placement='top'>
                                                    <AlertCircle src={InfoCircle} />
                                                </InfoToolTip>
                                            </SubtotalStart>
                                            <TextField
                                                label=""
                                                type="number"
                                                value={tier.upper_price_limit || ""}
                                                onChange={(e) => handleInputChange(e, tier.id || tier.tempId)}
                                                sx={{
                                                    ['z-index']: '2',
                                                    width: '155px',
                                                    border: 'none',
                                                    mr: 2,
                                                    "& input[type='number']::-webkit-outer-spin-button, & input[type='number']::-webkit-inner-spin-button": {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                    "& input[type='number']": {
                                                        MozAppearance: 'textfield',
                                                    },
                                                    "& input": {
                                                        padding: '0px 0px 3px 45px',
                                                    },
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            border: "none",
                                                        },
                                                    },
                                                    "& .MuiInputBase-input, .MuiOutlinedInput-input": {
                                                        boxSizing: "border-box",
                                                        boxShadow: "none",
                                                    },
                                                }}
                                            />

                                        </InputContainer>
                                    </SubtotalContainer>
                                    {tier.subtotalError && (
                                        <Typography color='error' variant='caption'>
                                            <ErrorMessage>
                                                {tier.subtotalError}
                                            </ErrorMessage>
                                        </Typography>
                                    )}
                                </div>

                                <div>
                                    <SubtotalContainer>
                                        <Text>
                                            Prep Time
                                        </Text>
                                        <Select
                                            value={tier.prep_minutes}
                                            onChange={(e) => handlePrepTimeChange(e, tier.id || tier.tempId)}
                                            displayEmpty
                                            IconComponent={() => (<Icon src={Chevron_Down} style={{ width: '24px', height: '24px', paddingRight: '8px' }} alt='outline_minus_button' />)} // Set the custom icon component here
                                            sx={{
                                                mr: 2,
                                                width: '200px',
                                                height: '47px',
                                                gap: '8px',
                                                borderRadius: '8px',
                                                border: '1px 0px 0px 0px',
                                                opacity: '0px', // Adjust opacity if you want it visible
                                            }}
                                        >
                                            {Array.from({ length: 20 }, (_, i) => 25 + i * 5).map((min) => (
                                                <MenuItem key={min} value={min}>
                                                    {min} minutes
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </SubtotalContainer>
                                    {tier.prepTimeError && (
                                        <Typography color='error' variant='caption'>
                                            <ErrorMessage>
                                                {tier.prepTimeError}
                                            </ErrorMessage>
                                        </Typography>
                                    )}
                                </div>

                                <IconButton style={{ paddingTop: '28px' }} data-testid={'removeTier'} onClick={() => removeTier(tier.id, tier.tempId)}>
                                    <Icon src={outline_minus_button} iconSize='20' alt='outline_minus_button' />
                                </IconButton>
                            </Box>
                            {displayTiers[index + 1] && <Divider />}
                        </div>
                    ))}
                </Box>
                <AddTier onClick={addTier} >Add Tier</AddTier>
            </DialogContent>
            <DialogActions sx={{ padding: 0 }}>
                <Footer>
                    <Button onClick={toggleModal}><Cancel>Cancel</Cancel> </Button>
                    <Button sx={{ padding: 0 }} onClick={handleSave} variant='contained'>
                        <PrimaryButton data-testid={'save'}>Save</PrimaryButton>
                    </Button>
                </Footer>
            </DialogActions>
        </Dialog>
    )
}

export default OrderThrottleEditor
